// Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import '../styles/auth/Auth.css';
import { login, register } from '../services/authService';
import { getS3ImageUrl } from '../services/s3Service';
import { getCachedImage } from '../utils/cmsCache';
import { REGIONES, getComunasPorRegion } from '../data/regionesYComunas';

// Imagen lateral por defecto (se reemplaza desde el CMS: "Página Login - Imagen lateral")
const LOGIN_IMAGE_DEFAULT = 'https://achipo-front-statics.s3.us-east-2.amazonaws.com/public/kirby.jpeg';

const Login = () => {
  // undefined = consultando CMS (sin imagen); luego URL del CMS o la de respaldo
  const [loginImage, setLoginImage] = useState(() => {
    const c = getCachedImage('page_hero_login');
    return c === undefined ? undefined : (c || LOGIN_IMAGE_DEFAULT);
  });
  useEffect(() => {
    let cancelado = false;
    getS3ImageUrl('page_hero_login')
      .then((url) => { if (!cancelado) setLoginImage(url || LOGIN_IMAGE_DEFAULT); })
      .catch(() => { if (!cancelado) setLoginImage(LOGIN_IMAGE_DEFAULT); });
    return () => { cancelado = true; };
  }, []);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [username, setUsername] = useState('');
  const [nombre, setNombre] = useState('');
  const [apellidoPaterno, setApellidoPaterno] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [rut, setRut] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [genero, setGenero] = useState('');
  const [region, setRegion] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [pais, setPais] = useState('Chile');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Mensajes de URL (?verified=1, ?error=token_invalid) o state del redirect
  useEffect(() => {
    const verified = searchParams.get('verified');
    const errParam = searchParams.get('error');
    const stateMsg = location.state?.message;
    if (verified === '1') {
      setSuccess('Correo verificado exitosamente. Ya puedes iniciar sesión.');
      setError('');
    } else if (errParam === 'token_invalid' || errParam === 'token_missing') {
      setError('El enlace de verificación ha expirado o no es válido. Por favor, solicita uno nuevo.');
    } else if (stateMsg) {
      setSuccess(stateMsg);
    }
  }, [searchParams, location.state]);

  // Colores ACHIPO (alineados con Header, Footer y resto del sitio)
  const colors = {
    primary: '#1a1a1a',
    secondary: '#555',
    accent: '#111111',
    accentLight: '#333333',
    lightGray: '#f8f9fa',
    mediumGray: '#eaeaea',
    darkGray: '#333',
    white: '#ffffff',
    blueLight: '#e8f4ff',
    green: '#00aa55',
    error: '#dc2626',
    errorBg: '#fef2f2'
  };

  // Comunas según la región seleccionada (lista completa de Chile desde regionesYComunas)
  const comunasPorRegion = region ? getComunasPorRegion(region) : [];

  // Función para validar RUT chileno
  const validarRUT = (rut) => {
    // Limpiar el RUT (quitar puntos y guiones)
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '').trim();
    
    // Verificar formato básico (debe tener al menos 7 dígitos + 1 dígito verificador)
    if (rutLimpio.length < 8 || rutLimpio.length > 9) {
      return false;
    }

    // Separar número y dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toUpperCase();

    // Verificar que el cuerpo sean solo números
    if (!/^\d+$/.test(cuerpo)) {
      return false;
    }

    // Verificar que el dígito verificador sea un número o K
    if (!/^[0-9K]$/.test(dv)) {
      return false;
    }

    // Calcular el dígito verificador correcto
    let suma = 0;
    let multiplicador = 2;

    // Recorrer el cuerpo del RUT de derecha a izquierda
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      suma += parseInt(cuerpo[i]) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }

    const resto = suma % 11;
    let dvCalculado = 11 - resto;

    if (dvCalculado === 11) {
      dvCalculado = '0';
    } else if (dvCalculado === 10) {
      dvCalculado = 'K';
    } else {
      dvCalculado = dvCalculado.toString();
    }

    // Comparar el dígito verificador
    return dv === dvCalculado;
  };

  // Función para formatear RUT (agregar puntos y guión)
  const formatearRUT = (rut) => {
    // Limpiar el RUT
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '').trim();
    
    if (rutLimpio.length <= 1) {
      return rutLimpio;
    }

    // Separar cuerpo y dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toUpperCase();

    // Formatear el cuerpo con puntos
    let cuerpoFormateado = '';
    for (let i = cuerpo.length - 1, j = 0; i >= 0; i--, j++) {
      if (j > 0 && j % 3 === 0) {
        cuerpoFormateado = '.' + cuerpoFormateado;
      }
      cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    }

    return cuerpoFormateado + '-' + dv;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Usar email o username como email para login
      const email = username.includes('@') ? username : username;
      
      const data = await login(email, password);
      
      if (data.user) {
        setSuccess(`¡Bienvenido de vuelta, ${data.user.nombre || data.user.email}!`);
        setIsLoading(false);
        
        // Disparar evento personalizado para que Header se actualice inmediatamente
        window.dispatchEvent(new Event('userLogin'));
        
        // Redireccionar al inicio después de 1 segundo
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    } catch (err) {
      const msg = err.code === 'email_not_verified'
        ? 'Debes verificar tu correo electrónico antes de iniciar sesión. Revisa tu bandeja de entrada.'
        : (err.message || 'Credenciales incorrectas. Verifica tu email y contraseña.');
      setError(msg);
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    
    // Validaciones
    if (!nombre.trim()) {
      setError('El nombre es requerido');
      setIsLoading(false);
      return;
    }

    if (!apellidoPaterno.trim()) {
      setError('El apellido paterno es requerido');
      setIsLoading(false);
      return;
    }

    if (!telefono.trim()) {
      setError('El número de teléfono es requerido');
      setIsLoading(false);
      return;
    }

    // Validar formato de teléfono (8-9 dígitos para Chile)
    const telefonoLimpio = telefono.replace(/\s/g, '').replace(/\+56/g, '');
    if (!/^[0-9]{8,9}$/.test(telefonoLimpio)) {
      setError('El número de teléfono debe tener 8 o 9 dígitos');
      setIsLoading(false);
      return;
    }

    if (!email.trim()) {
      setError('El email es requerido');
      setIsLoading(false);
      return;
    }

    if (!rut.trim()) {
      setError('El RUT es requerido');
      setIsLoading(false);
      return;
    }

    if (!validarRUT(rut)) {
      setError('El RUT ingresado no es válido. Verifica el formato y el dígito verificador.');
      setIsLoading(false);
      return;
    }

    if (!fechaNacimiento) {
      setError('La fecha de nacimiento es requerida');
      setIsLoading(false);
      return;
    }

    if (!genero) {
      setError('El género para competir es requerido');
      setIsLoading(false);
      return;
    }

    if (!region.trim()) {
      setError('La región es requerida');
      setIsLoading(false);
      return;
    }

    if (!ciudad.trim()) {
      setError('La ciudad es requerida');
      setIsLoading(false);
      return;
    }

    if (!pais.trim()) {
      setError('El país es requerido');
      setIsLoading(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setIsLoading(false);
      return;
    }

    // Limpiar RUT (sin puntos ni guión) para usarlo como ID
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '').trim().toUpperCase();

    // Registrar usuario en el backend
    try {
      const userData = {
        email: email.trim(),
        password,
        nombre: nombre.trim(),
        apellidoPaterno: apellidoPaterno.trim(),
        rut: rutLimpio, // RUT sin puntos ni guión
        telefono: telefonoLimpio,
        fechaNacimiento: fechaNacimiento,
        genero: genero === 'hombre' ? 'M' : 'F', // Mapear a 'M' o 'F' para el backend
        region: region.trim(),
        ciudad: ciudad.trim(),
        pais: pais.trim(),
      };
      
      await register(userData);
      
      setSuccess(`¡Cuenta creada exitosamente! Te hemos enviado un correo a tu email para validar tu cuenta. Revisa tu bandeja de entrada (y carpeta de spam) antes de iniciar sesión.`);
      
      // Cambiar a modo login para que pueda iniciar sesión después de verificar (sin redirigir)
      setIsLoginMode(true);
      setUsername(userData.email);
      setEmail(userData.email);
      setPassword('');
      setConfirmPassword('');
      setIsLoading(false);
    } catch (error) {
      // Mejorar mensaje de error para el usuario
      let errorMessage = error.message || 'Error al registrar usuario. Intenta nuevamente.';
      
      // Si el error menciona problemas de base de datos, mostrar mensaje más claro
      if (errorMessage.includes('base de datos') || errorMessage.includes('configuración') || errorMessage.includes('columna')) {
        errorMessage = '⚠️ ' + errorMessage + ' Por favor, notifica este error al equipo técnico.';
      }
      
      setError(errorMessage);
      setIsLoading(false);
      
      // También loggear el error completo para debugging
      console.error('Error completo al registrar usuario:', error);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setError('');
    setSuccess('');
    setUsername('');
    setNombre('');
    setApellidoPaterno('');
    setTelefono('');
    setEmail('');
    setRut('');
    setFechaNacimiento('');
    setGenero('');
    setRegion('');
    setCiudad('');
    setPais('Chile');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="auth-page" style={{ color: colors.primary }}>
      <Header />
      
      <div className="auth-shell">
        <aside
          className="auth-aside"
          aria-hidden="true"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.2) 45%, rgba(0,0,0,.9) 100%)${loginImage ? `, url(${loginImage})` : ''}` }}
        >
          <span className="ach-eyebrow-outline">Forjados en fuerza</span>
          <p className="ach-display auth-aside-title">
            Unidos en la <span>plataforma</span>
          </p>
          <ul className="auth-aside-list">
            <li>Inscríbete en competencias</li>
            <li>Gestiona tu afiliación</li>
            <li>Revisa tus marcas y el ranking</li>
          </ul>
        </aside>
        <div className="auth-card">
          {/* Header del formulario */}
          <div style={{ 
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            <img src="/achipo.jpg" alt="" className="auth-logo" />
            <h2 className="ach-display auth-title">
              {isLoginMode ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </h2>
            <p style={{ 
              color: colors.secondary, 
              fontSize: '0.95em',
              lineHeight: '1.5'
            }}>
              {isLoginMode 
                ? 'Accede a tu cuenta para continuar' 
                : 'Únete a la comunidad de powerlifting'}
            </p>
          </div>

          {/* Mensajes de estado */}
          {error && (
            <div style={{ 
              backgroundColor: colors.errorBg,
              color: colors.error,
              padding: '12px 16px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '0.9em',
              borderLeft: `3px solid ${colors.error}`
            }}>
              ⚠️ {error}
            </div>
          )}

          {success && (
            <div style={{ 
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              padding: '12px 16px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '0.9em',
              borderLeft: `3px solid ${colors.green}`
            }}>
              ✅ {success}
            </div>
          )}

          <form onSubmit={isLoginMode ? handleLogin : handleSignup}>
            {/* Campo Usuario/Email (solo en login) */}
            {isLoginMode ? (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                color: colors.primary, 
                marginBottom: '8px', 
                fontSize: '0.9em',
                fontWeight: '500'
              }}>
                  Usuario o Email
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${colors.mediumGray}`,
                  borderRadius: '6px',
                  fontSize: '0.95em',
                  color: colors.primary,
                  backgroundColor: colors.white,
                  transition: 'border-color 0.2s ease'
                }}
                  placeholder="usuario@ejemplo.com"
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                />
              </div>
            ) : (
              <>
                {/* Campos de Nombre Separados (solo en registro) */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      color: colors.primary, 
                      marginBottom: '8px', 
                      fontSize: '0.9em',
                      fontWeight: '500'
                    }}>
                      Nombre <span style={{ color: colors.accent }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `1px solid ${colors.mediumGray}`,
                        borderRadius: '6px',
                        fontSize: '0.95em',
                        color: colors.primary,
                        backgroundColor: colors.white,
                        transition: 'border-color 0.2s ease'
                      }}
                      placeholder="Juan"
                      onFocus={(e) => e.target.style.borderColor = colors.accent}
                      onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      color: colors.primary, 
                      marginBottom: '8px', 
                      fontSize: '0.9em',
                      fontWeight: '500'
                    }}>
                      Apellido Paterno <span style={{ color: colors.accent }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={apellidoPaterno}
                      onChange={(e) => setApellidoPaterno(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `1px solid ${colors.mediumGray}`,
                        borderRadius: '6px',
                        fontSize: '0.95em',
                        color: colors.primary,
                        backgroundColor: colors.white,
                        transition: 'border-color 0.2s ease'
                      }}
                      placeholder="González"
                      onFocus={(e) => e.target.style.borderColor = colors.accent}
                      onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Campo RUT (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  RUT <span style={{ color: colors.accent }}>*</span>
                </label>
                <input
                  type="text"
                  value={rut}
                  onChange={(e) => {
                    let valor = e.target.value;
                    // Permitir solo números, puntos, guiones y K
                    valor = valor.replace(/[^0-9.\-kK]/g, '');
                    setRut(valor);
                  }}
                  onBlur={(e) => {
                    // Formatear automáticamente al perder el foco
                    if (rut && validarRUT(rut)) {
                      setRut(formatearRUT(rut));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="12.345.678-9"
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  required
                />
                {rut && !validarRUT(rut) && rut.length > 0 && (
                  <span style={{ 
                    fontSize: '0.75em', 
                    color: colors.error,
                    marginTop: '4px',
                    display: 'block'
                  }}>
                    ⚠️ RUT inválido
                  </span>
                )}
              </div>
            )}

            {/* Campo Teléfono (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Teléfono <span style={{ color: colors.accent }}>*</span>
                </label>
                <input
                  type="tel"
                  value={telefono}
                  onChange={(e) => {
                    // Permitir solo números, espacios y +
                    let valor = e.target.value.replace(/[^0-9+\s]/g, '');
                    setTelefono(valor);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="912345678"
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                />
              </div>
            )}

            {/* Campo Fecha de Nacimiento (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Fecha de Nacimiento <span style={{ color: colors.accent }}>*</span>
                </label>
                <input
                  type="date"
                  value={fechaNacimiento}
                  onChange={(e) => setFechaNacimiento(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                />
              </div>
            )}

            {/* Campo Género para Competir (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Género para Competir <span style={{ color: colors.accent }}>*</span>
                </label>
                <select
                  value={genero}
                  onChange={(e) => setGenero(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                >
                  <option value="">Selecciona un género</option>
                  <option value="hombre">Hombre</option>
                  <option value="mujer">Mujer</option>
                </select>
              </div>
            )}

            {/* Campo Región (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Región <span style={{ color: colors.accent }}>*</span>
                </label>
                <select
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    setCiudad(''); // Limpiar ciudad cuando cambia la región
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                >
                  <option value="">Selecciona una región</option>
                  {REGIONES.map(reg => (
                    <option key={reg} value={reg}>{reg}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Campo Comuna (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Comuna <span style={{ color: colors.accent }}>*</span>
                </label>
                {region ? (
                  <select
                    value={ciudad}
                    onChange={(e) => setCiudad(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '6px',
                      fontSize: '0.95em',
                      color: colors.primary,
                      backgroundColor: colors.white,
                      transition: 'border-color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.accent}
                    onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                    required
                  >
                    <option value="">Selecciona una comuna</option>
                    {comunasPorRegion.map(comuna => (
                      <option key={comuna} value={comuna}>{comuna}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={ciudad}
                    disabled
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '6px',
                      fontSize: '0.95em',
                      color: colors.secondary,
                      backgroundColor: colors.lightGray,
                      cursor: 'not-allowed'
                    }}
                    placeholder="Primero selecciona una región para ver comunas"
                  />
                )}
              </div>
            )}

            {/* Campo País (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  País <span style={{ color: colors.accent }}>*</span>
                </label>
                <input
                  type="text"
                  value={pais}
                  onChange={(e) => setPais(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="Chile"
                onFocus={(e) => e.target.style.borderColor = colors.accent}
                onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                required
              />
            </div>
            )}

            {/* Campo Email (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Email <span style={{ color: colors.accent }}>*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="tu@email.com"
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                />
              </div>
            )}

            {/* Campo Contraseña */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                color: colors.primary, 
                marginBottom: '8px', 
                fontSize: '0.9em',
                fontWeight: '500'
              }}>
                Contraseña
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={isLoginMode ? "current-password" : "new-password"}
                  style={{
                    width: '100%',
                    padding: '12px 44px 12px 12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="••••••••"
                  onFocus={(e) => e.target.style.borderColor = colors.accent}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: colors.secondary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em'
                  }}
                  tabIndex={-1}
                  title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? 'Ocultar' : 'Ver'}
                </button>
              </div>
            </div>

            {/* Campo Confirmar Contraseña (solo en registro) */}
            {!isLoginMode && (
              <div style={{ marginBottom: '30px' }}>
                <label style={{ 
                  display: 'block', 
                  color: colors.primary, 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  Confirmar Contraseña
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    style={{
                      width: '100%',
                      padding: '12px 44px 12px 12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '6px',
                      fontSize: '0.95em',
                      color: colors.primary,
                      backgroundColor: colors.white,
                      transition: 'border-color 0.2s ease'
                    }}
                    placeholder="••••••••"
                    onFocus={(e) => e.target.style.borderColor = colors.accent}
                    onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: colors.secondary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}
                    tabIndex={-1}
                    title={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showConfirmPassword ? 'Ocultar' : 'Ver'}
                  </button>
                </div>
              </div>
            )}

            {/* Enlace de recuperación de contraseña (solo en login) */}
            {isLoginMode && (
              <div style={{ 
                marginTop: '10px', 
                marginBottom: '20px',
                textAlign: 'right',
                width: '100%'
              }}>
                <Link
                  to="/forgot-password"
                  style={{
                    color: colors.accent,
                    fontSize: '0.9em',
                    textDecoration: 'none',
                    fontWeight: '600',
                    display: 'inline-block',
                    padding: '6px 0',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}

            {/* Botón de submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="ach-pill ach-pill--red auth-submit"
            >
              {isLoading ? (
                <span>⏳ Procesando... El servidor puede tardar hasta 1 minuto.</span>
              ) : (
                <span>{isLoginMode ? 'Iniciar Sesión' : 'Crear Cuenta'}</span>
              )}
            </button>

            {/* Enlace para cambiar modo */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: '25px', 
              paddingTop: '20px',
              borderTop: `1px solid ${colors.mediumGray}`,
              color: colors.secondary,
              fontSize: '0.9em'
            }}>
              <p style={{ marginBottom: '10px' }}>
                {isLoginMode 
                  ? '¿No tienes una cuenta?' 
                  : '¿Ya tienes una cuenta?'}
              </p>
              <button
                type="button"
                onClick={toggleMode}
                className="auth-link"
              >
                {isLoginMode ? 'Crear cuenta aquí' : 'Iniciar sesión aquí'}
              </button>
            </div>

            {/* Link para volver al inicio */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px'
            }}>
              <Link 
                to="/"
                style={{
                  color: colors.secondary,
                  fontSize: '0.9em',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.color = colors.accent;
                  e.target.style.textDecoration = 'underline';
                }}
                onMouseOut={(e) => {
                  e.target.style.color = colors.secondary;
                  e.target.style.textDecoration = 'none';
                }}
              >
                <span>←</span>
                Volver al inicio
              </Link>
            </div>
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login;